// @flow

import { combineReducers } from 'redux';
import { LOCATION_CHANGE } from 'react-router-redux';
import {
  FETCH_MESSAGES,
  FETCH_MESSAGES_SUCCESS,
  FETCH_MESSAGES_FAILURE,
  SEND_MESSAGE,
  SEND_MESSAGE_SUCCESS,
  SEND_MESSAGE_FAILURE,
  FETCH_PREVIOUS_MESSAGES,
  FETCH_PREVIOUS_MESSAGES_PENDING,
  FETCH_PREVIOUS_MESSAGES_SUCCESS,
  FETCH_PREVIOUS_MESSAGES_FAILURE,
  FETCH_NEW_MESSAGES_SUCCESS,
} from '../actions/messages';
import * as Immutable from 'immutable';
import type { State } from './index';
import type { ThreadMessage } from '../types';

type StatusState = {
  thread?: string,
  lastReadIndex?: number,
  total?: number,
  scrollPosition?: number,
};
export type MessagesState = {
  isFetching: boolean,
  isFetchingPrevious: boolean,
  isFetchingPreviousPending: boolean,
  isError: boolean,
  isSending: boolean,
  isSendError: boolean,
  byId: Array<number>,
  status: StatusState,
  items: Immutable.Map<string, ThreadMessage>,
};

const items = (state = Immutable.Map(), action) => {
  switch (action.type) {
    case FETCH_MESSAGES_SUCCESS:
      return Immutable.Map(action.response.entities.messages);
    case FETCH_PREVIOUS_MESSAGES_SUCCESS:
    case FETCH_NEW_MESSAGES_SUCCESS:
      return state.merge(Immutable.Map(action.response.entities.messages));
    default:
      return state;
  }
};

const byId = (state = [], action) => {
  switch (action.type) {
    case FETCH_MESSAGES_SUCCESS:
      return action.response.result;
    case FETCH_PREVIOUS_MESSAGES_SUCCESS:
      return Immutable.Set([...action.response.result, ...state]).toArray();
    case FETCH_NEW_MESSAGES_SUCCESS:
      return Immutable.Set([...state, ...action.response.result]).toArray();
    default:
      return state;
  }
};

const isFetching = (state = false, action) => {
  switch (action.type) {
    case FETCH_MESSAGES:
      return true;
    case FETCH_MESSAGES_SUCCESS:
    case FETCH_MESSAGES_FAILURE:
      return false;
    default:
      return state;
  }
};

const isFetchingPrevious = (state = false, action) => {
  switch (action.type) {
    case FETCH_PREVIOUS_MESSAGES:
      return true;
    case FETCH_PREVIOUS_MESSAGES_SUCCESS:
    case FETCH_PREVIOUS_MESSAGES_FAILURE:
      return false;
    default:
      return state;
  }
};

const isFetchingPreviousPending = (state = false, action) => {
  switch (action.type) {
    case FETCH_PREVIOUS_MESSAGES_PENDING:
      return true;
    case FETCH_PREVIOUS_MESSAGES_SUCCESS:
    case FETCH_PREVIOUS_MESSAGES_FAILURE:
      return false;
    default:
      return state;
  }
};

const isError = (state = null, action) => {
  switch (action.type) {
    case FETCH_MESSAGES_FAILURE:
      return true;
    default:
      return state;
  }
};

const isSending = (state = false, action) => {
  switch (action.type) {
    case SEND_MESSAGE:
      return true;
    case SEND_MESSAGE_SUCCESS:
    case SEND_MESSAGE_FAILURE:
      return false;
    default:
      return state;
  }
};

const isSendError = (state = false, action) => {
  switch (action.type) {
    case SEND_MESSAGE_FAILURE:
      return true;
    default:
      return state;
  }
};

const status = (state = {}, action) => {
  switch (action.type) {
    case FETCH_MESSAGES_SUCCESS:
      return {
        ...state,
        thread: action.thread,
        total: action.total,
        lastReadIndex: action.status.lastReadIndex,
      };
    case FETCH_PREVIOUS_MESSAGES_SUCCESS:
      return {
        ...state,
        scrollPosition: action.scrollPosition,
      };
    case FETCH_NEW_MESSAGES_SUCCESS:
      return {
        ...state,
        total: action.total,
      };
    case LOCATION_CHANGE:
      if (action.payload.pathname === '/threads') {
        return {};
      } else {
        return state;
      }
    default:
      return state;
  }
};

export default combineReducers({
  items,
  byId,
  isFetching,
  isFetchingPrevious,
  isFetchingPreviousPending,
  isError,
  isSending,
  isSendError,
  status,
});

export const getIsFetching = (state: State) => state.messages.isFetching;
export const getIsFetchingPrevious = (state: State) =>
  state.messages.isFetchingPrevious;
export const getIsFetchingPreviousPending = (state: State) =>
  state.messages.isFetchingPreviousPending;
export const getIsError = (state: State) => state.messages.isError;
export const getIsSending = (state: State) => state.messages.isSending;
export const getIsSendError = (state: State) => state.messages.isSendError;
export const getTotal = (state: State) => state.messages.status.total;
export const getCurrentScrollPosition = (state: State) =>
  state.messages.status.scrollPosition;

export const getCurrentThread = (state: State) => state.messages.status.thread;
export const getLastReadIndex = (state: State) =>
  state.messages.status.lastReadIndex;

export const getLastIndex = (state: State): ?number =>
  state.messages.items.map(m => m.index).max();

export const getMessages = (state: State) => {
  const { byId, items } = state.messages;

  return Immutable.List(byId.map(id => items.get(id.toString()))).sortBy(
    m => m.index
  );
};



// WEBPACK FOOTER //
// ./src/modules/messaging/reducers/messages.js